import React, { createContext, useContext, useEffect, useState } from 'react';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { EstabelecimentosService } from '../../../estabelecimentos/system-admin/Service';
import { useAppContext } from '../../../../contexts/AppContext';

const service = new EstabelecimentosService();
const DEFAULT_CONFIRMATION_MESSAGE =
    '✅ Agendamento realizado com sucesso!\\nSessão finalizada.';

interface EstabFormParams {
    timezone: string;
    setTimezone: React.Dispatch<React.SetStateAction<string>>;
    name: string;
    setName: React.Dispatch<React.SetStateAction<string>>;
    imageId: string;
    setImageId: React.Dispatch<React.SetStateAction<string>>;
    street: string;
    setStreet: React.Dispatch<React.SetStateAction<string>>;
    number: string;
    setNumber: React.Dispatch<React.SetStateAction<string>>;
    complement: string;
    setComplement: React.Dispatch<React.SetStateAction<string>>;
    neighborhood: string;
    setNeighborhood: React.Dispatch<React.SetStateAction<string>>;
    city: string;
    setCity: React.Dispatch<React.SetStateAction<string>>;
    state: string;
    setState: React.Dispatch<React.SetStateAction<string>>;
    zipCode: string;
    setZipCode: React.Dispatch<React.SetStateAction<string>>;
    twitter: string;
    setTwitter: React.Dispatch<React.SetStateAction<string>>;
    addressUrl: string;
    setAddressUrl: React.Dispatch<React.SetStateAction<string>>;
    facebook: string;
    setFacebook: React.Dispatch<React.SetStateAction<string>>;
    instagram: string;
    setInstagram: React.Dispatch<React.SetStateAction<string>>;
    phoneNumber: string;
    setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
    isPhoneNumberWhatsapp: boolean;
    setIsPhoneNumberWhatsapp: React.Dispatch<React.SetStateAction<boolean>>;
    whatsappSchedulingConfirmationMessage: string;
    setWhatsappSchedulingConfirmationMessage: React.Dispatch<React.SetStateAction<string>>;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const EstabFormContext = createContext<EstabFormParams | undefined>(undefined);

export const useEstabFormContext = () => {
    const context = useContext(EstabFormContext);
    if (!context) {
        throw new Error('useEstabFormContext must be used within a EstabFormProvider');
    }
    return context;
};

export const EstabFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { estabSession, reloadEstab } = useEstabsContext();
    const { errorSnackbar, successSnackbar } = useAppContext();

    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [timezone, setTimezone] = useState('America/Sao_Paulo');
    const [street, setStreet] = useState('');
    const [number, setNumber] = useState('');
    const [complement, setComplement] = useState('');
    const [addressUrl, setAddressUrl] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [description, setDescription] = useState('');
    const [imageId, setImageId] = useState('');
    const [twitter, setTwitter] = useState('');
    const [facebook, setFacebook] = useState('');
    const [instagram, setInstagram] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isPhoneNumberWhatsapp, setIsPhoneNumberWhatsapp] = useState(false);
    const [whatsappSchedulingConfirmationMessage, setWhatsappSchedulingConfirmationMessage] = useState(
        DEFAULT_CONFIRMATION_MESSAGE
    );

    useEffect(() => {
        if (!!estabSession?._id) {
            setName(estabSession?.name || '');
            setTimezone(estabSession?.timezone || 'America/Sao_Paulo');
            setStreet(estabSession?.address?.street || '');
            setNumber(estabSession?.address?.number || '');
            setComplement(estabSession?.address?.complement || '');
            setNeighborhood(estabSession?.address?.neighborhood || '');
            setCity(estabSession?.address?.city || '');
            setState(estabSession?.address?.state || '');
            setAddressUrl(estabSession?.address?.addressUrl || '');
            setZipCode(estabSession?.address?.zipCode || '');
            setDescription(estabSession?.description || '');
            setTwitter(estabSession?.twitter || '');
            setFacebook(estabSession?.facebook || '');
            setInstagram(estabSession?.instagram || '');
            setPhoneNumber(estabSession?.phoneNumber || '');
            setIsPhoneNumberWhatsapp(estabSession?.isPhoneNumberWhatsapp || false);
            setImageId(estabSession?.imageId || '');
            setWhatsappSchedulingConfirmationMessage(
                estabSession?.whatsappSchedulingConfirmationMessage || DEFAULT_CONFIRMATION_MESSAGE
            );


            setLoading(false);
        }
    }, [estabSession]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
            setLoading(true);

            await service.update(estabSession._id, {
                name,
                timezone,
                address: {
                    street,
                    number,
                    complement,
                    neighborhood,
                    city,
                    state,
                    zipCode,
                    addressUrl,
                },
                description,
                imageId,
                facebook,
                twitter,
                instagram,
                phoneNumber,
                isPhoneNumberWhatsapp,
                whatsappSchedulingConfirmationMessage,
            });

            successSnackbar('Estabelecimento alterado com sucesso!');
            reloadEstab();
        } catch (e) {
            console.log(e);
            errorSnackbar((e as any)?.response?.data?.message === 'timezoneInvalido'
                ? 'Informe um fuso horário IANA válido, como America/Manaus ou Europe/Lisbon.'
                : 'Ocorreu um erro ao alterar o estabelecimento.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <EstabFormContext.Provider
            value={{
                name,
                setName,
                timezone,
                setTimezone,
                street,
                setStreet,
                twitter,
                setTwitter,
                number,
                setNumber,
                complement,
                addressUrl,
                setAddressUrl,
                setComplement,
                neighborhood,
                setNeighborhood,
                city,
                setCity,
                state,
                setState,
                zipCode,
                setZipCode,
                facebook,
                setFacebook,
                instagram,
                setInstagram,
                phoneNumber,
                setPhoneNumber,
                imageId,
                setImageId,
                handleSubmit,
                loading,
                setLoading,
                isPhoneNumberWhatsapp,
                setIsPhoneNumberWhatsapp,
                whatsappSchedulingConfirmationMessage,
                setWhatsappSchedulingConfirmationMessage,
            }}
        >
            {children}
        </EstabFormContext.Provider>
    );
};
